import { Injectable, Injector} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { NotifierTypeEnum } from 'src/app/models/common/notifier-type.enum';
import { NotifierModel } from 'src/app/models/common/notifier.model';
import { Fernet } from 'fernet-ts';
import { EncryptKeyModel } from 'src/app/models/web/encrypt-key.model';
import { AuthserviceService } from '../authservice.service';
import { AppSessionStorageService } from '../utils/session-storage.service';
import { EmqxRecieveMessageModel } from 'src/app/models/common/emqx-receive-message.model';
import { EmqxHelper } from './emqx-helper';
import { Utils } from 'src/app/utils/utils';

export enum ImageSizeType {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}


export type NotifierFunctionType<T> = (data: T) => void;

export class NotifierHelper<T> {
  private lock: boolean = false;
  private readonly notifierMap: Map<string, NotifierFunctionType<T>> =
    new Map();
  private list: NotifierFunctionType<T>[] = [];

  async add(key: string, func: NotifierFunctionType<T>) {
    if (!this.lock) {
      this.lock = true;
      this.notifierMap.set(key, func);
      this.list = [...this.notifierMap.values()];
      this.lock = false;
    } else {
      await Utils.delay(100);
      await this.add(key, func);
    }
  }

  async notify(data: T) {
    if (!this.lock) {
      this.lock = true;
      for (const func of this.list ?? []) {
        func(data);
      }
      this.lock = false;
    } else {
      await Utils.delay(100);
      await this.notify(data);
    }
  }

  async remove(key: string) {
    if (!this.lock) {
      this.lock = true;
      this.notifierMap.delete(key);
      this.list = [...this.notifierMap.values()];
      this.lock = false;
    } else {
      await Utils.delay(100);
      await this.remove(key);
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  //Created behaviour subject to notify other components about any changes
  private readonly notifier: NotifierHelper<NotifierModel> =
    new NotifierHelper<NotifierModel>();

  //Created behaviour subject to notify other components about any changes
  private readonly emqxMessageNotifier: NotifierHelper<EmqxRecieveMessageModel> =
    new NotifierHelper<EmqxRecieveMessageModel>();

  private static fernet: Fernet | undefined | null;
  private emqxHelper!: EmqxHelper;

  constructor(private injector: Injector) {
    this.emqxHelper = EmqxHelper.instance;
  }

  async subscribeMqttTopic(topic: string): Promise<boolean> {
    return this.emqxHelper.subscribe(topic);
  }

  async unSubscribeMqttTopic(topic:string): Promise<void>{
    return this.emqxHelper.unsubscribe(topic);
  }

  subscribeMqttMessages(
    key: string,
    func: NotifierFunctionType<EmqxRecieveMessageModel>
  ) {
    this.emqxMessageNotifier.add(key, func);
  }

  sendMqttMessage(topic: string, payload: string): boolean {
    return this.emqxHelper.publishMessage(topic, payload);
  }

  disconnectMqtt(): void {
    this.emqxHelper.disconnect();
  }

  /**
   * Please call this function to notify other components about new neveiw added
   * @param model - Added review
   */
  notifyLoggedIn(isForSignInRequiredClick: boolean) {
    this.notifier.notify(
      new NotifierModel(NotifierTypeEnum.LoggedIn, isForSignInRequiredClick)
    );
  }
  /**
   * Please call this function to notify other components about new review added
   * @param model - Added review
   */
  notifyLogout() {
    this.notifier.notify(new NotifierModel(NotifierTypeEnum.LoggedOut, null));
  }

  /**
   * To get the user entered text from the event in html
   */
  inputEventValue(event: Event): string {
    return (event.target as HTMLInputElement | null | undefined)?.value ?? '';
  }

  /**
   * It will converts the text entered into the text field to an int number
   */
  inputEventValueToInt(event: Event, defaultValue: number = 0): number {
    try {
      const num = parseInt(this.inputEventValue(event));
      return Number.isNaN(num) ? defaultValue : num;
    } catch (_) {}
    return defaultValue;
  }

  /**
   * It will converts the text entered into the text field to a float number
   */
  inputEventValueToNumber(event: Event, defaultValue: number = 0): number {
    try {
      const num = parseFloat(this.inputEventValue(event));
      return Number.isNaN(num) ? defaultValue : num;
    } catch (_) {}
    return defaultValue;
  }

  isEncryptionKeyFetched(): boolean {
    return CommonService.fernet != undefined && CommonService.fernet != null;
  }

  async updateKey(model: EncryptKeyModel | undefined | null) {
    try {
      if (model?.value) {
        model.value.key = this.removeAdditionalStrings(
          model.value.key,
          model.value.time
        );
        CommonService.fernet = await Fernet.getInstance(model.value.key);
        const storageService = this.injector.get(AppSessionStorageService);
        if (model?.auth?.access_token) {
          await storageService.setup();
          storageService.setToken(model?.auth, true);
          this.setupMqtt(model.auth?.webhook);
          this.injector.get(AuthserviceService).fetchUserDetails();
        } else {
          storageService.removeToken();
        }
      }
    } catch (_) {}
  }

  async setupMqtt(webhook: string[] | null | undefined): Promise<void> {
    try {
      if (webhook) {
        this.emqxHelper.setup(
          this.emqxMessageNotifier,
          await this.decryptSingleString(webhook[2]),
          await this.decryptSingleString(webhook[0])
        );
      }
    } catch (_) {}
  }

  async encrypt(data: any): Promise<string> {
    const encrypted_data = await CommonService.fernet?.encrypt(
      JSON.stringify(data)
    );
    return encrypted_data?.toString() ?? '';
  }

  // FUNCTION TO DECRYPT RECIEVED ENCRYPTED DATA
  async decryptData(encryptedDataAsMap: any): Promise<any> {
    return this.decrypt(encryptedDataAsMap?.data);
  }

  // FUNCTION TO DECRYPT RECIEVED ENCRYPTED DATA
  async decrypt(encryptedData: any): Promise<any> {
    try {
      if (encryptedData) {
        const decrypted = await CommonService.fernet?.decrypt(encryptedData);
        return JSON.parse(decrypted?.replaceAll("'", '"') ?? '{}');
      }
    } catch (_) {}
    return encryptedData;
  }

  // FUNCTION TO DECRYPT RECIEVED ENCRYPTED DATA
  async decryptSingleString(encryptedData: string): Promise<string> {
    try {
      if (encryptedData) {
        return (
          (await CommonService.fernet?.decrypt(encryptedData)) ?? encryptedData
        );
      }
    } catch (_) {}
    return encryptedData;
  }

  private removeAdditionalStrings(
    key: string,
    current_time_millis: string
  ): string {
    let third_and_fourth_from_right_num = parseInt(
      current_time_millis.substring(2, 4)
    );
    if (third_and_fourth_from_right_num > 30) {
      third_and_fourth_from_right_num = 30;
    }

    const time_length = current_time_millis.length;
    let middle_index = Math.floor(time_length / 2) + 1;
    let middle_num = parseInt(current_time_millis.charAt(middle_index));
    if (middle_num < 4) {
      middle_num = 4;
    }

    let covered = middle_num * third_and_fourth_from_right_num;
    middle_index += middle_num;
    if (middle_index >= time_length) {
      middle_index -= time_length;
    }

    for (let i = 0; i < middle_num + 1; ++i) {
      if (middle_index < 0) {
        middle_index = time_length - 1;
      }
      let next_index = middle_index + 1;
      if (next_index >= time_length) {
        next_index = 0;
      }
      let index = parseInt(
        current_time_millis.charAt(middle_index) +
          current_time_millis.charAt(next_index)
      );
      if (index > 30) {
        index =
          parseInt(current_time_millis.charAt(middle_index)) +
          parseInt(current_time_millis.charAt(next_index));
      }
      --middle_index;

      key =
        key.substring(0, index + covered) +
        key.substring(index + covered + third_and_fourth_from_right_num);
      covered -= third_and_fourth_from_right_num;
    }

    key = atob(key);
    return key;
  }
}
